import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChartBar } from '@phosphor-icons/react';

import { Spinner, Button, EmptyState } from '@/components/ui';
import { AnalyticsChart } from '@/components/features/AnalyticsChart/AnalyticsChart';
import { PieChart } from '@/components/features/AnalyticsChart/charts/PieChart/PieChart';
import { HeatmapChart } from '@/components/features/AnalyticsChart/charts/HeatmapChart/HeatmapChart';
import { useForm, useFormAnalytics } from '@/api';
import { useAuthStore } from '@/stores';
import { ROUTES } from '@/constants';

import styles from './FormAnalytics.module.css';

const FIELD_TYPE_LABEL: Record<string, string> = {
    short_text: 'Texto corto',
    long_text: 'Texto largo',
    single_choice: 'Opcion unica',
    multiple_choice: 'Opcion multiple',
    dropdown: 'Desplegable',
    linear_scale: 'Escala lineal',
    date: 'Fecha',
    number: 'Numero',
    time: 'Hora',
    file_upload: 'Archivo',
    choice_grid: 'Grilla opcion unica',
    checkbox_grid: 'Grilla opcion multiple',
};

type DatePreset = 'today' | '7d' | '30d' | 'all';

function getPresetDates(preset: DatePreset): { date_from?: string; date_to?: string } {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    switch (preset) {
        case 'today':
            return { date_from: today, date_to: today };
        case '7d': {
            const d = new Date(now);
            d.setDate(d.getDate() - 7);
            return { date_from: d.toISOString().split('T')[0], date_to: today };
        }
        case '30d': {
            const d = new Date(now);
            d.setDate(d.getDate() - 30);
            return { date_from: d.toISOString().split('T')[0], date_to: today };
        }
        case 'all':
        default:
            return {};
    }
}

export function FormAnalytics() {
    const { formSlug } = useParams<{ userSlug: string; formSlug: string }>();
    const navigate = useNavigate();
    const { getUser } = useAuthStore();
    const user = getUser();

    const [datePreset, setDatePreset] = useState<DatePreset>('30d');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [showCustom, setShowCustom] = useState(false);

    const dateFilters = showCustom
        ? { date_from: customFrom || undefined, date_to: customTo || undefined }
        : getPresetDates(datePreset);

    const { data: form, isLoading: formLoading, error: formError } = useForm(formSlug || '');
    const { data: analytics, isLoading: analyticsLoading, error: analyticsError, refetch } = useFormAnalytics(
        formSlug || '',
        dateFilters
    );

    useEffect(() => {
        if (form) {
            document.title = `Analiticas — ${form.title} — Ratio`;
        }
    }, [form]);

    const isOwner = form && user && form.author.id === user.id;

    if (formLoading) {
        return (
            <div className={styles.loading}>
                <Spinner size="lg" />
            </div>
        );
    }

    if (formError || !form) {
        return (
            <div className={styles.errorPage}>
                <h2 className={styles.errorTitle}>Formulario no encontrado</h2>
                <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                    Volver al inicio
                </Button>
            </div>
        );
    }

    if (!isOwner) {
        return (
            <div className={styles.errorPage}>
                <h2 className={styles.errorTitle}>Acceso restringido</h2>
                <p className={styles.errorText}>Solo el autor puede ver las analiticas.</p>
                <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                    Volver al inicio
                </Button>
            </div>
        );
    }

    if (analyticsLoading) {
        return (
            <div className={styles.loading}>
                <Spinner size="lg" />
            </div>
        );
    }

    if (analyticsError || !analytics) {
        return (
            <div className={styles.errorPage}>
                <h2 className={styles.errorTitle}>Error cargando analiticas</h2>
                <p className={styles.errorText}>Algo salio mal.</p>
                <Button variant="primary" onClick={() => refetch()}>
                    Reintentar
                </Button>
            </div>
        );
    }

    const allFields = form.sections?.length > 0
        ? form.sections.flatMap((s) => s.fields)
        : form.fields;

    return (
        <div className={styles.page}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Volver">
                <ArrowLeft size={18} />
                Volver
            </button>

            <div className={styles.header}>
                <h1 className={styles.pageTitle}>Analiticas</h1>
                <p className={styles.formName}>{form.title}</p>
            </div>

            {/* Date range filter */}
            <div className={styles.dateFilter}>
                <div className={styles.presetBtns}>
                    {(['today', '7d', '30d', 'all'] as DatePreset[]).map((preset) => (
                        <button
                            key={preset}
                            type="button"
                            className={`${styles.presetBtn} ${!showCustom && datePreset === preset ? styles.presetActive : ''}`}
                            onClick={() => { setDatePreset(preset); setShowCustom(false); }}
                        >
                            {preset === 'today' && 'Hoy'}
                            {preset === '7d' && '7 dias'}
                            {preset === '30d' && '30 dias'}
                            {preset === 'all' && 'Todo'}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={`${styles.presetBtn} ${showCustom ? styles.presetActive : ''}`}
                        onClick={() => setShowCustom(true)}
                    >
                        Personalizado
                    </button>
                </div>

                {showCustom && (
                    <div className={styles.customRange}>
                        <input
                            type="date"
                            className={styles.dateInput}
                            value={customFrom}
                            onChange={(e) => setCustomFrom(e.target.value)}
                        />
                        <span className={styles.dateSep}>a</span>
                        <input
                            type="date"
                            className={styles.dateInput}
                            value={customTo}
                            onChange={(e) => setCustomTo(e.target.value)}
                        />
                    </div>
                )}
            </div>

            {/* Summary */}
            <div className={styles.statsRow}>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{analytics.total_responses}</span>
                    <span className={styles.statLabel}>Total respuestas</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{allFields.length}</span>
                    <span className={styles.statLabel}>Preguntas</span>
                </div>
            </div>

            {/* Responses over time */}
            {analytics.responses_over_time.length > 0 && (
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>Respuestas por dia</h2>
                    <AnalyticsChart
                        type="bar"
                        entries={analytics.responses_over_time.map((d) => ({
                            label: new Date(d.date).toLocaleDateString('es', { day: 'numeric', month: 'short' }),
                            count: d.count,
                        }))}
                        total={analytics.total_responses}
                    />
                </div>
            )}

            {/* Per-field summaries */}
            {analytics.total_responses === 0 ? (
                <EmptyState
                    icon={<ChartBar weight="duotone" />}
                    title="Sin datos aun"
                    description="Las analiticas apareceran cuando recibas respuestas"
                    actionLabel="Compartir formulario"
                    onAction={() => navigate(`/${form.author.slug}/f/${form.slug}/compartir`)}
                />
            ) : (
                <div className={styles.fieldSummaries}>
                    {analytics.field_summaries.map((fs) => {
                        const fieldType = fs.field_type;
                        const isSingleChoice = fieldType === 'single_choice' || fieldType === 'dropdown';
                        const isMultiChoice = fieldType === 'multiple_choice';
                        const isText = ['short_text', 'long_text'].includes(fieldType);
                        const isGrid = fieldType === 'choice_grid' || fieldType === 'checkbox_grid';
                        const isTime = fieldType === 'time';
                        const hasAverage = typeof fs.summary.average === 'number';

                        // Get rows/columns from form field definition
                        const fieldDef = allFields.find((f) => f.id === fs.field_id);
                        const gridRows = Array.isArray(fieldDef?.config['rows']) ? fieldDef.config['rows'] as string[] : [];
                        const gridCols = Array.isArray(fieldDef?.config['columns']) ? fieldDef.config['columns'] as string[] : [];

                        return (
                            <div key={fs.field_id} className={styles.fieldCard}>
                                <div className={styles.fieldCardHeader}>
                                    <h3 className={styles.fieldLabel}>{fs.label}</h3>
                                    <span className={styles.fieldType}>{FIELD_TYPE_LABEL[fieldType] ?? fieldType}</span>
                                </div>

                                {hasAverage && (
                                    <div className={styles.averageRow}>
                                        <span className={styles.averageValue}>
                                            {(fs.summary.average ?? 0).toFixed(1)}
                                        </span>
                                        <span className={styles.averageLabel}>promedio</span>
                                    </div>
                                )}

                                {/* Pie chart for single choice */}
                                {isSingleChoice && fs.summary.distribution && (
                                    <PieChart
                                        entries={Object.entries(fs.summary.distribution).map(([label, count]) => ({
                                            label,
                                            count,
                                        }))}
                                        total={fs.summary.total_answers ?? analytics.total_responses}
                                    />
                                )}

                                {/* Bar chart for multiple choice / linear_scale */}
                                {(isMultiChoice || fieldType === 'linear_scale') && fs.summary.distribution && (
                                    <AnalyticsChart
                                        type="horizontal-bar"
                                        entries={Object.entries(fs.summary.distribution).map(([label, count]) => ({
                                            label,
                                            count,
                                        }))}
                                        total={fs.summary.total_answers ?? analytics.total_responses}
                                    />
                                )}

                                {/* Heatmap for grid */}
                                {isGrid && fs.summary.heatmap && gridRows.length > 0 && gridCols.length > 0 && (
                                    <HeatmapChart
                                        heatmap={fs.summary.heatmap}
                                        rows={gridRows}
                                        columns={gridCols}
                                        total={fs.summary.total_answers}
                                    />
                                )}

                                {/* Text answers */}
                                {isText && (
                                    <div className={styles.textAnswers}>
                                        <p className={styles.textAnswersCount}>
                                            {fs.summary.total_answers ?? 0} respuestas
                                        </p>
                                        {fs.summary.recent_answers && fs.summary.recent_answers.length > 0 && (
                                            <div className={styles.recentAnswers}>
                                                {fs.summary.recent_answers.slice(0, 5).map((ans, i) => (
                                                    <div key={i} className={styles.recentAnswer}>
                                                        <span className={styles.recentAnswerText}>{ans}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Time distribution */}
                                {isTime && fs.summary.time_distribution && (
                                    <AnalyticsChart
                                        type="horizontal-bar"
                                        entries={Object.entries(fs.summary.time_distribution).map(([label, count]) => ({
                                            label,
                                            count,
                                        }))}
                                        total={fs.summary.total_answers ?? analytics.total_responses}
                                    />
                                )}

                                {fieldType === 'number' && fs.summary.distribution && (
                                    <AnalyticsChart
                                        type="horizontal-bar"
                                        entries={Object.entries(fs.summary.distribution).map(([label, count]) => ({
                                            label,
                                            count,
                                        }))}
                                        total={analytics.total_responses}
                                    />
                                )}

                                {fieldType === 'date' && fs.summary.distribution && (
                                    <AnalyticsChart
                                        type="bar"
                                        entries={Object.entries(fs.summary.distribution).map(([label, count]) => ({
                                            label,
                                            count,
                                        }))}
                                        total={analytics.total_responses}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
